import React, { useState } from 'react'
import { ArrowLeft, RefreshCw, Send } from 'lucide-react'
import { Badge } from './Badge'
import { getDefaultProvisioningDetails } from '../api/api-client'
import { ProvisioningStatus } from '../api/schema'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProvisioningDocument {
  id:           string
  type:         'ProvisioningForm' | 'LoA' | 'Other'
  filename:     string
  uploaded_at:  string
  download_url:
  
  string
}

export interface ProvisioningDetails {
  asn:                      string
  ip_prefixes:              string[]
  latency_sensitivity_ms:   number
  model_of_routers:         string[]
  location_of_idc:          string
  tunnel_endpoints: {
    router_designation: string
    tunnel_endpoint_ip: string
  }[]
  netflow_details: {
    netflow_ip:            string
    netflow_version:       string
    netflow_sampling_rate: string
  }[]
  whitelisted_ips:          string[]
  monitoring_ips:           string[]
  protocol_and_ports:       string[]
  ids_ips_or_gslb_presence: string[]
  additional_information:   string
}

const emptyForm: ProvisioningDetails = {
  asn:                      '',
  ip_prefixes:              [],
  latency_sensitivity_ms:   0,
  model_of_routers:         [],
  location_of_idc:          '',
  tunnel_endpoints:         [{ router_designation: '', tunnel_endpoint_ip: '' }],
  netflow_details:          [{ netflow_ip: '', netflow_version: '', netflow_sampling_rate: '' }],
  whitelisted_ips:          [],
  monitoring_ips:           [],
  protocol_and_ports:       [],
  ids_ips_or_gslb_presence: [],
  additional_information:   '',
}

export interface ProvisioningSubmission {
  id:              number       
  submitted_at:    string
  status:          'submitted' | 'acknowledged' | 'declined' | 'ready' | 'superseded' | 'failed'
  decline_reason:  string | null
  documents:       ProvisioningDocument[]
  details?:        ProvisioningDetails  
}

export interface ProvisioningTimelineProps {
  provisioningId:          string
  status:         ProvisioningStatus
  location:       string
  dirtyBw:        number
  cleanBw:        number
  periodFrom:     string
  periodUntil:    string
  history:        ProvisioningSubmission[]
  role:           'buyer' | 'seller'
  onSubmit?:      (details: ProvisioningDetails) => Promise<void>
  onAcknowledge?: (submissionId: string) => Promise<void>
  onDecline?:     (submissionId: string, reason: string) => Promise<void>
  onReady?:       (submissionId: string) => Promise<void>
  onFail?:        (submissionId: string, reason: string) => Promise<void>
  onBack?:        () => void
  onRefresh?:     () => Promise<void>
  downloadLoa?:   (submissionId: number) => Promise<void>
  showHeader?: boolean
}

// ─── Mock history ─────────────────────────────────────────────────────────────

export function getMockHistory(provisioningId: string, locationId?: number): ProvisioningSubmission[] {
  if (provisioningId === 'rfq-002' && locationId === 1) return [
    {
      id:            2,
      submitted_at:  '2026-06-14T10:22:00Z',
      status:        'submitted',
      decline_reason: null,
      documents:     [
        { id: 'd1', type: 'ProvisioningForm', filename: 'form_sub2.pdf',
          uploaded_at: '2026-06-14T10:22:00Z', download_url: '#' },
        { id: 'd2', type: 'LoA', filename: 'loa_sub2.pdf',
          uploaded_at: '2026-06-14T10:22:00Z', download_url: '#' },
      ],
      details: {
        asn:                      'ASN00009',
        ip_prefixes:              ['203.0.113.0/24'],
        latency_sensitivity_ms:   10,
        model_of_routers:         ['Cisco ASR 9000'],
        location_of_idc:          'Singapore DC1',
        tunnel_endpoints: [
          { router_designation: 'PE-1', tunnel_endpoint_ip: '192.0.2.1' },
        ],
        netflow_details: [
          { netflow_ip: '10.0.0.1', netflow_version: 'v9', netflow_sampling_rate: '1:1000' },
        ],
        whitelisted_ips:          ['10.0.0.0/8'],
        monitoring_ips:           ['10.0.0.2'],
        protocol_and_ports:       ['TCP/443', 'UDP/53'],
        ids_ips_or_gslb_presence: ['Palo Alto NGFW'],
        additional_information:   'Primary link via SingTel',
      },
    },
    {
      id:            1,
      submitted_at:  '2026-06-10T09:30:00Z',
      status:        'declined',
      decline_reason: 'Too broad — use /24 or smaller.',
      documents: [
        { id: 'd3', type: 'ProvisioningForm', filename: 'form_sub1.pdf',
          uploaded_at: '2026-06-10T09:30:00Z', download_url: '#' },
      ],
      details: {
        asn:                      'ASN00009',
        ip_prefixes:              ['10.0.0.0/8'],
        latency_sensitivity_ms:   10,
        model_of_routers:         ['Cisco ASR 9000'],
        location_of_idc:          'Singapore DC1',
        tunnel_endpoints: [
          { router_designation: 'PE-1', tunnel_endpoint_ip: '192.0.2.5' },
        ],
        netflow_details:          [],
        whitelisted_ips:          [],
        monitoring_ips:           [],
        protocol_and_ports:       [],
        ids_ips_or_gslb_presence: [],
        additional_information:   '',
      },
    },
  ]

  if (provisioningId === 'rfq-003' && locationId === 1) return [
    {
      id:            1,
      submitted_at:  '2026-06-18T08:00:00Z',
      status:        'submitted',
      decline_reason: null,
      documents: [
        { id: 'd4', type: 'ProvisioningForm', filename: 'form_rfq003_sub1.pdf',
          uploaded_at: '2026-06-18T08:00:00Z', download_url: '#' },
        { id: 'd5', type: 'LoA', filename: 'loa_rfq003_sub1.pdf',
          uploaded_at: '2026-06-18T08:00:00Z', download_url: '#' },
      ],
      details: {
        asn:                      'ASN00042',
        ip_prefixes:              ['198.51.100.0/24'],
        latency_sensitivity_ms:   5,
        model_of_routers:         ['Juniper MX204'],
        location_of_idc:          'Singapore DC2',
        tunnel_endpoints: [
          { router_designation: 'PE-2', tunnel_endpoint_ip: '203.0.113.5' },
        ],
        netflow_details: [
          { netflow_ip: '10.1.0.1', netflow_version: 'v10', netflow_sampling_rate: '1:500' },
        ],
        whitelisted_ips:          ['192.168.0.0/16'],
        monitoring_ips:           ['10.1.0.2'],
        protocol_and_ports:       ['TCP/80', 'TCP/443'],
        ids_ips_or_gslb_presence: ['Fortinet FortiGate'],
        additional_information:   'Secondary link via M1',
      },
    },
  ]

  return []
}
// ─── Document card ────────────────────────────────────────────────────────────

function DocCard({ doc }: { doc: ProvisioningDocument }) {
  const icon  = doc.type === 'LoA' ? '📋' : '📄'
  const label = doc.type === 'LoA' ? 'Letter of Authorisation'
    : doc.type === 'ProvisioningForm' ? 'Provisioning Form'
    : doc.filename

  return (
    <div style={{
      display:'flex', flexDirection:'column', gap:'0.375rem',
      padding:'0.625rem 0.875rem', minWidth:'160px',
      background:'var(--color-background-primary)',
      border:'1px solid var(--color-border-secondary)',
      borderRadius:'0.5rem',
    }}>
      <div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--color-text-primary)' }}>
        {icon} {label}
      </div>
      <div style={{ fontSize:'0.7rem', color:'var(--color-text-tertiary)',
        fontFamily:'var(--font-mono)', wordBreak:'break-all' }}>
        {doc.filename}
      </div>
      <a href={doc.download_url} download={doc.filename} style={{
        display:'inline-flex', alignItems:'center', gap:'0.25rem',
        fontSize:'0.75rem', fontWeight:600, color:'#0D8F7A',
        textDecoration:'none', marginTop:'0.125rem',
      }}>
        ↓ Download
      </a>
    </div>
  )
}

// ─── Submission card ──────────────────────────────────────────────────────────




function SubmissionCard({

  
  
  sub, role, downloadLoa, onAcknowledge, onDecline, onReady, onFail,
}: {
  sub:            ProvisioningSubmission
  role:           'buyer' | 'seller'
  downloadLoa?:   (submission_id: number) => Promise<void>
  onAcknowledge?: (id: number) => Promise<void>
  onDecline?:     (id: number, reason: string) => Promise<void>
  onReady?: (id: number) => Promise<void>
  onFail?:     (id: number, reason: string) => Promise<void>
}) {
  const [actioning,     setActioning]     = useState(false)
  const [localStatus,   setLocalStatus]   = useState(sub.status)
  const [showDecline,   setShowDecline]   = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [showFailReasonInput, setShowFailReasonInput]   = useState(false)
  const [failReason, setFailReason] = useState('')
  const [actionError,   setActionError]   = useState<string | null>(null)

  const statusColor =
  localStatus === 'declined'       ? '#EF4444' :
  localStatus === 'failed'         ? '#EF4444' :
  localStatus === 'superseded'     ? '#94A3B8' :
  localStatus === 'ready'          ? '#0D8F7A' :
  localStatus === 'acknowledged'   ? '#0D8F7A' :
  '#F59E0B'  // submitted = amber

 async function handleAcknowledge() {
  setActioning(true); setActionError(null)
  try {
    await onAcknowledge?.(sub.id)
    setLocalStatus('acknowledged')   // ← was 'Acknowledged'
  } catch (e) {
    setActionError(e instanceof Error ? e.message : 'Failed.')
  } finally { setActioning(false) }
}

async function handleDecline() {
  if (!declineReason.trim()) return
  setActioning(true); setActionError(null)
  try {
    await onDecline?.(sub.id, declineReason.trim())
    setLocalStatus('declined')       // ← was 'Declined'
    setShowDecline(false)
  } catch (e) {
    setActionError(e instanceof Error ? e.message : 'Failed.')
  } finally { setActioning(false) }
}
 async function handleReady() {
  setActioning(true); setActionError(null)
  try {
    await onReady?.(sub.id)
    setLocalStatus('ready')   // ← was 'Acknowledged'
  } catch (e) {
    setActionError(e instanceof Error ? e.message : 'Failed.')
  } finally { setActioning(false) }
}

async function handleFail() {
  if (!failReason.trim()) return
  setActioning(true); setActionError(null)
  try {
    await onFail?.(sub.id, failReason.trim())
    setLocalStatus('failed')       // ← was 'Declined'
    setShowFailReasonInput(false)
  } catch (e) {
    setActionError(e instanceof Error ? e.message : 'Failed.')
  } finally { setActioning(false) }
}

async function handleDownloadLoA() {
  setActioning(true); setActionError(null)
  try {
    await downloadLoa?.(sub.id)
  } catch (e) {
    setActionError(e instanceof Error ? e.message : 'Failed.')
  } finally { setActioning(false) }
}

  return (
    <div style={{
  borderRadius:'0.75rem', overflow:'hidden',
  border:`1px solid ${statusColor}40`,
  background: localStatus === 'superseded'
    ? '#F9FAFB'
    : 'var(--color-background-primary)',
  opacity: localStatus === 'superseded' ? 0.5 : 0.7,
}}>

      {/* Header */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'0.75rem 1rem',
        background:`${statusColor}12`,
        borderBottom:`1px solid ${statusColor}30`,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <span style={{
            width:'0.625rem', height:'0.625rem', borderRadius:'50%',
            background: statusColor, flexShrink:0, display:'inline-block',
          }} />
          <span style={{ fontSize:'0.875rem', fontWeight:600, color:'var(--color-text-primary)' }}>
            Submission #{sub.id+1}
          </span>
          <Badge status={localStatus} />
        </div>
        <span style={{ fontSize:'0.72rem', color:'var(--color-text-tertiary)' }}>
          {new Date(sub.submitted_at).toLocaleString('en-SG', {
            dateStyle:'medium', timeStyle:'short'
          })}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding:'0.875rem 1rem', display:'flex', flexDirection:'column', gap:'0.875rem' }}>

  {sub.details && (
    <>
      {/* Core fields */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
        <div>
          <div style={fieldLabel}>ASN</div>
          <div style={{ ...fieldValue, fontFamily:'var(--font-mono)' }}>{sub.details.asn}</div>
        </div>
        <div>
          <div style={fieldLabel}>Location of IDC</div>
          <div style={fieldValue}>{sub.details.location_of_idc}</div>
        </div>
        <div>
          <div style={fieldLabel}>Latency Sensitivity</div>
          <div style={fieldValue}>{sub.details.latency_sensitivity_ms} ms</div>
        </div>
        <div>
          <div style={fieldLabel}>Model of Routers</div>
          <div style={fieldValue}>{sub.details.model_of_routers.join(', ') || '—'}</div>
        </div>
      </div>

      {/* IP Prefixes */}
      {sub.details.ip_prefixes.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>IP Prefixes</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
            {sub.details.ip_prefixes.map((ip, i) => (
              <span key={i} style={pill}>{ip}</span>
            ))}
          </div>
        </div>
      )}

      {/* Tunnel Endpoints */}
      {sub.details.tunnel_endpoints.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>Tunnel Endpoints</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
            {sub.details.tunnel_endpoints.map((ep, i) => (
              <div key={i} style={{
                fontSize:'0.8rem', fontFamily:'var(--font-mono)',
                color:'var(--color-text-primary)',
              }}>
                {ep.router_designation} → {ep.tunnel_endpoint_ip}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Netflow */}
      {sub.details.netflow_details.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>Netflow</div>
          {sub.details.netflow_details.map((nf, i) => (
            <div key={i} style={{ fontSize:'0.8rem', color:'var(--color-text-secondary)',
              fontFamily:'var(--font-mono)' }}>
              {nf.netflow_ip} · v{nf.netflow_version} · {nf.netflow_sampling_rate}
            </div>
          ))}
        </div>
      )}

      {/* Whitelisted IPs */}
      {sub.details.whitelisted_ips.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>Whitelisted IPs</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
            {sub.details.whitelisted_ips.map((ip, i) => (
              <span key={i} style={pill}>{ip}</span>
            ))}
          </div>
        </div>
      )}

      {/* Monitoring IPs */}
      {sub.details.monitoring_ips.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>Monitoring IPs</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
            {sub.details.monitoring_ips.map((ip, i) => (
              <span key={i} style={pill}>{ip}</span>
            ))}
          </div>
        </div>
      )}

      {/* Protocols & Ports */}
      {sub.details.protocol_and_ports.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>Protocols & Ports</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
            {sub.details.protocol_and_ports.map((p, i) => (
              <span key={i} style={pill}>{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* IDS/IPS/GSLB */}
      {sub.details.ids_ips_or_gslb_presence.length > 0 && (
        <div>
          <div style={{ ...fieldLabel, marginBottom:'0.375rem' }}>IDS/IPS/GSLB</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
            {sub.details.ids_ips_or_gslb_presence.map((s, i) => (
              <span key={i} style={pill}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Additional info */}
      {sub.details.additional_information && (
        <div>
          <div style={fieldLabel}>Additional Information</div>
          <div style={{ fontSize:'0.8rem', color:'var(--color-text-secondary)' }}>
            {sub.details.additional_information}
          </div>
        </div>
      )}
    </>
  )}

  {/* Documents */}
  {sub.documents.length > 0 && (
    <div>
      <div style={{ ...fieldLabel, marginBottom:'0.5rem' }}>Documents</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem' }}>
        {sub.documents.map(doc => <DocCard key={doc.id} doc={doc} />)}
      </div>
    </div>
  )}


      {/* Decline reason */}
  {localStatus === 'declined' && sub.decline_reason && (
    <div style={{
      padding:'0.625rem 0.875rem',
      background:'#FEF2F2', border:'1px solid #FECACA',
      borderRadius:'0.5rem', fontSize:'0.8rem', color:'#B91C1C',
    }}>
      <span style={{ fontWeight:600 }}>Decline reason: </span>
      {sub.decline_reason}
    </div>
  )}

      <button onClick={handleDownloadLoA} disabled={actioning} style={{
        padding:'0.5rem 1.25rem', borderRadius:'0.375rem', border:'none',
        cursor: actioning ? 'not-allowed' : 'pointer',
        background: actioning ? '#94A3B8' : '#0D8F7A', color:'#fff',
        fontSize:'0.8rem', fontWeight:600,
      }}>
        Download LoA
      </button>

        {/* Seller actions — latest AwaitingAcknowledgement only */}
      {role === 'seller' && localStatus === 'submitted' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
            {actionError && (
              <p style={{ fontSize:'0.75rem', color:'#EF4444', margin:0 }}>{actionError}</p>
            )}
            {!showDecline ? (
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={handleAcknowledge} disabled={actioning} style={{
                  padding:'0.5rem 1.25rem', borderRadius:'0.375rem', border:'none',
                  cursor: actioning ? 'not-allowed' : 'pointer',
                  background: actioning ? '#94A3B8' : '#0D8F7A', color:'#fff',
                  fontSize:'0.8rem', fontWeight:600,
                }}>
                  {actioning ? 'Processing…' : 'Acknowledge'}
                </button>
                <button onClick={() => setShowDecline(true)} disabled={actioning} style={{
                  padding:'0.5rem 1.25rem', borderRadius:'0.375rem',
                  border:'1px solid #EF4444', cursor:'pointer',
                  background:'#fff', color:'#EF4444',
                  fontSize:'0.8rem', fontWeight:600,
                }}>
                  Decline
                </button>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                <textarea
                  placeholder="Reason for declining (required)..."
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  rows={2}
                  style={{
                    border:'1px solid #FECACA', borderRadius:'0.375rem',
                    padding:'0.5rem 0.75rem', fontSize:'0.875rem',
                    color:'#1E293B', outline:'none', resize:'vertical',
                  }}
                />
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button onClick={handleDecline}
                    disabled={actioning || !declineReason.trim()} style={{
                    padding:'0.375rem 1rem', borderRadius:'0.375rem', border:'none',
                    cursor: actioning || !declineReason.trim() ? 'not-allowed' : 'pointer',
                    background: actioning ? '#94A3B8' : '#EF4444', color:'#fff',
                    fontSize:'0.8rem', fontWeight:600,
                  }}>
                    {actioning ? 'Processing…' : 'Confirm Decline'}
                  </button>
                  <button onClick={() => { setShowDecline(false); setDeclineReason('') }} style={{
                    padding:'0.375rem 0.75rem', borderRadius:'0.375rem',
                    border:'1px solid #CBD5E1', background:'#fff',
                    color:'#475569', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {role === 'seller' && localStatus === 'acknowledged' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
            {actionError && (
              <p style={{ fontSize:'0.75rem', color:'#EF4444', margin:0 }}>{actionError}</p>
            )}
            {!showFailReasonInput ? (
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={handleReady} disabled={actioning} style={{
                  padding:'0.5rem 1.25rem', borderRadius:'0.375rem', border:'none',
                  cursor: actioning ? 'not-allowed' : 'pointer',
                  background: actioning ? '#94A3B8' : '#0D8F7A', color:'#fff',
                  fontSize:'0.8rem', fontWeight:600,
                }}>
                  {actioning ? 'Processing…' : 'Confirm Provisioning Ready'}
                </button>

                <button onClick={() => setShowFailReasonInput(true)} disabled={actioning} style={{
                  padding:'0.5rem 1.25rem', borderRadius:'0.375rem',
                  border:'1px solid #EF4444', cursor:'pointer',
                  background:'#fff', color:'#EF4444',
                  fontSize:'0.8rem', fontWeight:600,
                }}>
                  Unable to Fulfill
                </button>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                <textarea
                  placeholder="Reason for not fulfilling (required)..."
                  value={failReason}
                  onChange={e => setFailReason(e.target.value)}
                  rows={2}
                  style={{
                    border:'1px solid #FECACA', borderRadius:'0.375rem',
                    padding:'0.5rem 0.75rem', fontSize:'0.875rem',
                    color:'#1E293B', outline:'none', resize:'vertical',
                  }}
                />
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button onClick={handleFail}
                    disabled={actioning || !failReason.trim()} style={{
                    padding:'0.375rem 1rem', borderRadius:'0.375rem', border:'none',
                    cursor: actioning || !failReason.trim() ? 'not-allowed' : 'pointer',
                    background: actioning ? '#94A3B8' : '#EF4444', color:'#fff',
                    fontSize:'0.8rem', fontWeight:600,
                  }}>
                    {actioning ? 'Processing…' : 'Confirm Unable to Fulfill'}
                  </button>
                  <button onClick={() => { setShowFailReasonInput(false); setFailReason('') }} style={{
                    padding:'0.375rem 0.75rem', borderRadius:'0.375rem',
                    border:'1px solid #CBD5E1', background:'#fff',
                    color:'#475569', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export const ProvisioningTimeline: React.FC<ProvisioningTimelineProps> = ({
  provisioningId, status, location, dirtyBw, cleanBw, periodFrom, periodUntil,
  history: initialHistory, role, onSubmit, downloadLoa, onAcknowledge, onDecline, onFail, onReady, onBack, onRefresh, showHeader = true,
}) => {
  const [history,      setHistory]      = useState(initialHistory)
  const [showForm,     setShowForm]     = useState(false)
  const [submitting,   setSubmitting]   = useState(false)
  const [error,        setError]        = useState<string | null>(null)
  const [formData, setFormData] = useState<ProvisioningDetails>(emptyForm)
  const [profileLoading, setProfileLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await onRefresh?.()
    } finally {
      setRefreshing(false)
    }
  }
  console.log(history)
  const latest    = history[history.length -1]
  const canSubmit = role === 'buyer' && status !== "completed" && status !== "failed"

  function openForm() {
    // Pre-fill from buyer profile
  setProfileLoading(true)
  getDefaultProvisioningDetails(provisioningId)
    .then(defaults => {
      setFormData({
        asn:                      defaults.asn ?? '',
        ip_prefixes:              defaults.ip_prefixes,
        latency_sensitivity_ms:   defaults.latency_sensitivity_ms ?? 0,
        model_of_routers:         defaults.model_of_routers,
        location_of_idc:          defaults.location_of_idc ?? '',
        tunnel_endpoints:         defaults.tunnel_endpoints.length > 0
          ? defaults.tunnel_endpoints
          : [{ router_designation: '', tunnel_endpoint_ip: '' }],
        netflow_details:          defaults.netflow_details.length > 0
          ? defaults.netflow_details
          : [{ netflow_ip: '', netflow_version: '', netflow_sampling_rate: '' }],
        whitelisted_ips:          defaults.whitelisted_ips,
        monitoring_ips:           defaults.monitoring_ips,
        protocol_and_ports:       defaults.protocol_and_ports,
        ids_ips_or_gslb_presence: defaults.ids_ips_or_gslb_presence,
        additional_information:   defaults.additional_information ?? '',
      })
    })
    .catch(() => {})
    .finally(() => {
      setProfileLoading(false)
      setShowForm(true)
    })
}
async function handleSubmit() {
 if (!formData.asn.trim() || formData.ip_prefixes.length === 0) {
    setError('ASN and IP Prefixes are required.')
    return
  }
  setSubmitting(true); setError(null)
  try {
    await onSubmit?.(formData)   // ← pass full formData
    setHistory(prev => [{
      id:           prev.length > 0 ? prev[prev.length-1].id + 1 : 0,
      submitted_at: new Date().toISOString(),
      status:       'submitted',
      decline_reason: null,
      documents:    [],
      details:      formData,
    }, ...prev])
    setShowForm(false)
    setFormData(emptyForm)
  } catch (e) {
    setError(e instanceof Error ? e.message : 'Failed.')
  } finally { setSubmitting(false) }
}

 async function handleAcknowledge(submissionId: number) {
  await onAcknowledge?.(String(submissionId))
  setHistory(prev => prev.map(s =>
    s.id === submissionId ? { ...s, status: 'acknowledged' as const } : s
  ))
}

async function handleDecline(submissionId: number, reason: string) {
  await onDecline?.(String(submissionId), reason)
  setHistory(prev => prev.map(s =>
    s.id === submissionId ? { ...s, status: 'declined' as const, decline_reason: reason } : s
  ))
}

 async function handleReady(submissionId: number) {
  await onReady?.(String(submissionId))
  setHistory(prev => prev.map(s =>
    s.id === submissionId ? { ...s, status: 'ready' as const } : s
  ))
}

async function handleFail(submissionId: number, reason: string) {
  await onFail?.(String(submissionId), reason)
  setHistory(prev => prev.map(s =>
    s.id === submissionId ? { ...s, status: 'failed' as const, decline_reason: reason } : s
  ))
}

  return (
  <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

    {/* Header */}
    {showHeader && (
      <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
        {onBack && (
          <button onClick={onBack} style={{
            display:'flex', alignItems:'center', gap:'0.375rem',
            padding:'0.375rem 0.75rem', border:'1px solid var(--color-border-secondary)',
            borderRadius:'0.375rem', background:'var(--color-background-primary)',
            fontSize:'0.75rem', color:'var(--color-text-secondary)', cursor:'pointer'
          }}>
            <ArrowLeft size={13}/> Back
          </button>
        )}
        <h2 style={{ margin:0, fontSize:'1rem', fontWeight:500,
          color:'var(--color-text-primary)', flex:1 }}>
          Provisioning — {provisioningId} · {location}
        </h2>
        {<Badge status={status} />}
        {onRefresh && (
          <button onClick={handleRefresh} disabled={refreshing} style={{
            display:'flex', alignItems:'center', gap:'0.375rem',
            padding:'0.375rem 0.75rem', border:'1px solid var(--color-border-secondary)',
            borderRadius:'0.375rem', background:'var(--color-background-primary)',
            fontSize:'0.75rem', color:'var(--color-text-secondary)',
            cursor: refreshing ? 'not-allowed' : 'pointer'
          }}>
            <RefreshCw size={13}/> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
        {canSubmit && (
          <button onClick={openForm} style={{
            display:'flex', alignItems:'center', gap:'0.375rem',
            padding:'0.5rem 1.25rem', borderRadius:'0.375rem', border:'none',
            background:'#0D8F7A', color:'#fff',
            fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
          }}>
            <Send size={13}/>
            {!latest
    ? 'Submit Details'
    : latest.status === 'declined'
      ? 'Resubmit Form'
      : 'Update Submission'}
          </button>
        )}
      </div>
    )}

      {/* Request summary */}
      <div style={card}>
        <div style={sectionTitle}>Request Details</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem' }}>
          <Field label="Location"        value={location} />
          <Field label="Dirty Bandwidth" value={`${dirtyBw} Gbps`} />
          <Field label="Clean Bandwidth" value={`${cleanBw} Gbps`} />
          <Field label="From"            value={periodFrom} />
          <Field label="Until"           value={periodUntil} />
          <Field label="Submissions"     value={String(history.length)} />
        </div>
      </div>

      {/* Buyer submit form */}
      {/* Buyer submit form — updated to match API */}
{showForm && (
  <div style={{ ...card, border:'1.5px solid #0D8F7A', background:'#F0FDFA' }}>
    <div style={sectionTitle}>
      {latest ? `Resubmit — Submission #${history.length + 1}` : 'Submission #1'}
    </div>

    {latest?.status === 'declined' && latest.decline_reason && (
      <div style={{
        padding:'0.625rem 0.875rem', marginBottom:'1rem',
        background:'#FEF2F2', border:'1px solid #FECACA',
        borderRadius:'0.5rem', fontSize:'0.8rem', color:'#B91C1C',
      }}>
        <span style={{ fontWeight:600 }}>Previous decline reason: </span>
        {latest.decline_reason}
      </div>
    )}

    {profileLoading && (
      <p style={{ fontSize:'0.75rem', color:'var(--color-text-tertiary)', marginBottom:'1rem' }}>
        Loading profile…
      </p>
    )}

    {/* Section: Core */}
    <div style={{ marginBottom:'1rem' }}>
      <div style={{ ...sectionTitle, marginBottom:'0.75rem' }}>Network Details</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
        <FormField label="ASN" required>
          <input type="text" placeholder="e.g. ASN00009"
            value={formData.asn}
            onChange={e => setFormData(p => ({ ...p, asn: e.target.value }))}
            style={inputStyle} />
        </FormField>
        <FormField label="Location of IDC">
          <input type="text" placeholder="e.g. Singapore DC1"
            value={formData.location_of_idc}
            onChange={e => setFormData(p => ({ ...p, location_of_idc: e.target.value }))}
            style={inputStyle} />
        </FormField>
        <FormField label="IP Prefixes" hint="comma-separated" required>
          <input type="text" placeholder="e.g. 203.0.113.0/24, 198.51.100.0/24"
            value={formData.ip_prefixes}
            onChange={e => setFormData(p => ({...p, ip_prefixes: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
        <FormField label="Latency Sensitivity (ms)">
          <input type="number" placeholder="e.g. 10"
            value={formData.latency_sensitivity_ms}
            onChange={e => setFormData(p => ({ ...p, latency_sensitivity_ms: Number(e.target.value) }))}
            style={inputStyle} />
        </FormField>
        <FormField label="Model of Routers" hint="comma-separated">
          <input type="text" placeholder="e.g. Cisco ASR 9000"
            value={formData.model_of_routers}
            onChange={e => setFormData(p => ({...p, model_of_routers: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
        <FormField label="Whitelisted IPs" hint="comma-separated">
          <input type="text" placeholder="e.g. 10.0.0.0/8"
            value={formData.whitelisted_ips}
            onChange={e => setFormData(p => ({...p, whitelisted_ips: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
        <FormField label="Monitoring IPs" hint="comma-separated">
          <input type="text" placeholder="e.g. 10.0.0.2"
            value={formData.monitoring_ips}
            onChange={e => setFormData(p => ({...p, monitoring_ips: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
        <FormField label="Protocol & Ports" hint="comma-separated">
          <input type="text" placeholder="e.g. TCP/443, UDP/53"
            value={formData.protocol_and_ports}
            onChange={e => setFormData(p => ({...p, protocol_and_ports: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
        <FormField label="IDS/IPS/GSLB Presence" hint="comma-separated">
          <input type="text" placeholder="e.g. Palo Alto NGFW"
            value={formData.ids_ips_or_gslb_presence}
            onChange={e => setFormData(p => ({...p, ids_ips_or_gslb_presence: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
            style={inputStyle} />
        </FormField>
      </div>
    </div>

    {/* Section: Tunnel Endpoints */}
    <div style={{ marginBottom:'1rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:'0.75rem' }}>
        <div style={sectionTitle}>Tunnel Endpoints</div>
        <button onClick={() => setFormData(p => ({
          ...p, tunnel_endpoints: [...p.tunnel_endpoints,
            { router_designation: '', tunnel_endpoint_ip: '' }]
        }))} style={{
          fontSize:'0.75rem', color:'#0D8F7A', background:'none',
          border:'1px solid #0D8F7A', borderRadius:'0.375rem',
          padding:'0.25rem 0.625rem', cursor:'pointer',
        }}>
          + Add
        </button>
      </div>
      {formData.tunnel_endpoints.map((ep, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto',
          gap:'0.75rem', marginBottom:'0.5rem', alignItems:'end' }}>
          <FormField label={i === 0 ? 'Router Designation' : ''}>
            <input type="text" placeholder="e.g. PE-1"
              value={ep.router_designation}
              onChange={e => {
                const updated = [...formData.tunnel_endpoints]
                updated[i] = { ...updated[i], router_designation: e.target.value }
                setFormData(p => ({ ...p, tunnel_endpoints: updated }))
              }}
              style={inputStyle} />
          </FormField>
          <FormField label={i === 0 ? 'Tunnel Endpoint IP' : ''}>
            <input type="text" placeholder="e.g. 192.0.2.1"
              value={ep.tunnel_endpoint_ip}
              onChange={e => {
                const updated = [...formData.tunnel_endpoints]
                updated[i] = { ...updated[i], tunnel_endpoint_ip: e.target.value }
                setFormData(p => ({ ...p, tunnel_endpoints: updated }))
              }}
              style={inputStyle} />
          </FormField>
          {formData.tunnel_endpoints.length > 1 && (
            <button onClick={() => setFormData(p => ({
              ...p, tunnel_endpoints: p.tunnel_endpoints.filter((_, j) => j !== i)
            }))} style={{
              fontSize:'0.75rem', color:'#EF4444', background:'none',
              border:'1px solid #EF4444', borderRadius:'0.375rem',
              padding:'0.5rem 0.625rem', cursor:'pointer',
            }}>
              ✕
            </button>
          )}
        </div>
      ))}
    </div>

    {/* Section: Netflow */}
    <div style={{ marginBottom:'1rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:'0.75rem' }}>
        <div style={sectionTitle}>Netflow Details</div>
        <button onClick={() => setFormData(p => ({
          ...p, netflow_details: [...p.netflow_details,
            { netflow_ip: '', netflow_version: '', netflow_sampling_rate: '' }]
        }))} style={{
          fontSize:'0.75rem', color:'#0D8F7A', background:'none',
          border:'1px solid #0D8F7A', borderRadius:'0.375rem',
          padding:'0.25rem 0.625rem', cursor:'pointer',
        }}>
          + Add
        </button>
      </div>
      {formData.netflow_details.map((nf, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto',
          gap:'0.75rem', marginBottom:'0.5rem', alignItems:'end' }}>
          <FormField label={i === 0 ? 'Netflow IP' : ''}>
            <input type="text" placeholder="e.g. 10.0.0.1"
              value={nf.netflow_ip}
              onChange={e => {
                const updated = [...formData.netflow_details]
                updated[i] = { ...updated[i], netflow_ip: e.target.value }
                setFormData(p => ({ ...p, netflow_details: updated }))
              }}
              style={inputStyle} />
          </FormField>
          <FormField label={i === 0 ? 'Version' : ''}>
            <input type="text" placeholder="e.g. v9"
              value={nf.netflow_version}
              onChange={e => {
                const updated = [...formData.netflow_details]
                updated[i] = { ...updated[i], netflow_version: e.target.value }
                setFormData(p => ({ ...p, netflow_details: updated }))
              }}
              style={inputStyle} />
          </FormField>
          <FormField label={i === 0 ? 'Sampling Rate' : ''}>
            <input type="text" placeholder="e.g. 1:1000"
              value={nf.netflow_sampling_rate}
              onChange={e => {
                const updated = [...formData.netflow_details]
                updated[i] = { ...updated[i], netflow_sampling_rate: e.target.value }
                setFormData(p => ({ ...p, netflow_details: updated }))
              }}
              style={inputStyle} />
          </FormField>
          {formData.netflow_details.length > 1 && (
            <button onClick={() => setFormData(p => ({
              ...p, netflow_details: p.netflow_details.filter((_, j) => j !== i)
            }))} style={{
              fontSize:'0.75rem', color:'#EF4444', background:'none',
              border:'1px solid #EF4444', borderRadius:'0.375rem',
              padding:'0.5rem 0.625rem', cursor:'pointer',
            }}>
              ✕
            </button>
          )}
        </div>
      ))}
    </div>

    {/* Additional Info */}
    <div style={{ marginBottom:'1rem' }}>
      <FormField label="Additional Information">
        <textarea placeholder="Any additional notes..."
          value={formData.additional_information}
          onChange={e => setFormData(p => ({ ...p, additional_information: e.target.value }))}
          rows={2}
          style={{ ...inputStyle, resize:'vertical' }} />
      </FormField>
    </div>

    {error && (
      <p style={{ fontSize:'0.75rem', color:'#EF4444', marginBottom:'0.75rem' }}>{error}</p>
    )}

    <div style={{ display:'flex', gap:'0.5rem' }}>
      <button onClick={handleSubmit} disabled={submitting} style={{
        display:'flex', alignItems:'center', gap:'0.375rem',
        padding:'0.5rem 1.25rem', borderRadius:'0.375rem', border:'none',
        background: submitting ? '#94A3B8' : '#0D8F7A', color:'#fff',
        fontSize:'0.8rem', fontWeight:600,
        cursor: submitting ? 'not-allowed' : 'pointer',
      }}>
        <Send size={13}/> {submitting ? 'Submitting…' : 'Submit'}
      </button>
      <button onClick={() => setShowForm(false)} style={{
        padding:'0.5rem 1rem', borderRadius:'0.375rem',
        border:'1px solid #CBD5E1', background:'#fff',
        color:'#475569', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
      }}>
        Cancel
      </button>
    </div>
  </div>
)}

      {/* Timeline */}
      <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
        {history.length === 0 ? (
          <div style={{ ...card, textAlign:'center',
            color:'var(--color-text-tertiary)', fontSize:'0.875rem', padding:'2rem' }}>
            {role === 'buyer'
              ? 'No submissions yet. Click "Submit Details" to begin.'
              : 'No provisioning details submitted by buyer yet.'}
          </div>
        ) : (
          history.sort((sub_a, sub_b) => sub_b.id - sub_a.id).map((sub) => (
            <SubmissionCard
              key={sub.id}
              sub={sub}
              role={role}
              downloadLoa={downloadLoa}
              onAcknowledge={handleAcknowledge}
              onDecline={handleDecline}
              onReady={handleReady}
              onFail={handleFail}
              
            />
          ))
        )}
      </div>

    </div>
  )
}

function FormField({ label, hint, required, children }: {
  label:     string
  hint?:     string
  required?: boolean
  children:  React.ReactNode
}) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
      {label && (
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color:'#EF4444', marginLeft:'0.2rem' }}>*</span>}
          {hint && <span style={{ color:'var(--color-text-tertiary)', fontWeight:400,
            marginLeft:'0.375rem' }}>({hint})</span>}
        </label>
      )}
      {children}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background:'#fff', borderRadius:'0.75rem',
  border:'1px solid #E2E8F0', padding:'1.25rem',
}
const sectionTitle: React.CSSProperties = {
  fontSize:'0.75rem', fontWeight:700, color:'var(--color-text-tertiary)',
  textTransform:'uppercase', letterSpacing:'0.06em',
  marginBottom:'1rem', paddingBottom:'0.5rem',
  borderBottom:'1px solid #E2E8F0',
}
const fieldLabel: React.CSSProperties = {
  fontSize:'0.7rem', fontWeight:600, color:'var(--color-text-tertiary)',
  textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.25rem',
}
const fieldValue: React.CSSProperties = {
  fontSize:'0.875rem', color:'var(--color-text-primary)',
  fontWeight:500, wordBreak:'break-all',
}
const labelStyle: React.CSSProperties = {
  fontSize:'0.75rem', fontWeight:600, color:'var(--color-text-secondary)',
  textTransform:'uppercase', letterSpacing:'0.04em',
}
const inputStyle: React.CSSProperties = {
  border:'1px solid #CBD5E1', borderRadius:'0.375rem',
  padding:'0.5rem 0.75rem', fontSize:'0.875rem',
  color:'#1E293B', outline:'none', background:'#fff',
}

const pill: React.CSSProperties = {
  padding:'0.25rem 0.625rem', borderRadius:'0.375rem',
  background:'var(--color-background-secondary)',
  border:'1px solid var(--color-border-secondary)',
  fontSize:'0.8rem', color:'var(--color-text-primary)',
  fontFamily:'var(--font-mono)',
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      <div style={fieldValue}>{value || '—'}</div>
    </div>
  )
}

